// "My brand" [Mi marca] (M14): restaurant name, contact line, logo, 5 colours, heading and body font, price
// format. Set once in this browser (IndexedDB) and applied to every ad template.
// Built-in default (owner, 2026-10-12): Mister Jon's — the logo (public/brand/, made from the owner's file by
// scripts/make-brand-logo.ts), its colours and misterjon.com's fonts. Every new device starts with it; a brand
// someone already saved stays theirs; "Reset to default" goes back to it (keeping the contact line).
import { create } from 'zustand';
import { createStore, get, set, type UseStore } from 'idb-keyval';
import type { BrandData } from '../model/types';
import { DEFAULT_CURRENCY } from './fields';

export type BrandLogo = { bytes: Uint8Array; type: string; w: number; h: number };
export type BrandProfile = Omit<
  BrandData,
  'logoAsset' | 'logoW' | 'logoH' | 'logoLightAsset' | 'logoLightW' | 'logoLightH'
> & {
  logo: BrandLogo | null;
  /** Optional logo for light backgrounds (the default brand: the orange circle without the white lettering). */
  logoLight?: BrandLogo | null;
};

const hex = (h: string): [number, number, number] =>
  [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16) / 255) as [number, number, number];

/** The default brand's values; the logos are loaded by defaultBrand(). */
export const DEFAULT_BRAND: BrandProfile = {
  name: "Mister Jon's",
  contact: '',
  location: 'Panajachel · Sololá',
  logo: null,
  logoLight: null,
  colors: {
    primary: hex('#DA6637'), // the logo's orange circle (owner: #DA6637 from the logo)
    secondary: hex('#3D1C12'), // the logo's rooster brown
    accent: hex('#F65005'), // misterjon.com button orange
    background: hex('#FFFFFF'),
    text: hex('#242424'), // misterjon.com dark text
  },
  headingFont: 'Lora',
  bodyFont: 'Lato',
  useBrandFonts: false,
  currency: DEFAULT_CURRENCY,
};

export const DEFAULT_LOGO_FILES = {
  logo: 'brand/misterjons-logo.png',
  logoLight: 'brand/misterjons-mark.png',
};
const base = (): string => (import.meta.env?.BASE_URL as string | undefined) ?? '/';

async function fetchLogo(path: string): Promise<BrandLogo | null> {
  try {
    const res = await fetch(base() + path);
    if (!res.ok) return null;
    return await logoFromFile(await res.blob());
  } catch {
    return null;
  }
}

let defaultLogos: Promise<{ logo: BrandLogo | null; logoLight: BrandLogo | null }> | null = null;
/** The built-in brand with its logos (fetched once; null logos when offline and not cached). */
export async function defaultBrand(): Promise<BrandProfile> {
  defaultLogos ??= Promise.all([
    fetchLogo(DEFAULT_LOGO_FILES.logo),
    fetchLogo(DEFAULT_LOGO_FILES.logoLight),
  ]).then(([logo, logoLight]) => ({ logo, logoLight }));
  const logos = await defaultLogos;
  if (!logos.logo) defaultLogos = null; // try again next time
  return { ...DEFAULT_BRAND, colors: { ...DEFAULT_BRAND.colors }, ...logos };
}

/** A saved brand as stored: it keeps its own values; only fields added later fall back to the defaults. */
export function mergeSaved(saved: BrandProfile): BrandProfile {
  return {
    ...DEFAULT_BRAND,
    ...saved,
    colors: { ...DEFAULT_BRAND.colors, ...saved.colors },
    // A brand saved before the location line existed has none (never the default restaurant's).
    location: saved.location ?? '',
    // Never the default brand's light logo on someone else's brand.
    logoLight: saved.logoLight ?? null,
  };
}

let db: UseStore | null = null;
const store = () => (db ??= createStore('epb-brand', 'brand'));
const KEY = 'brand';

type BrandState = { brand: BrandProfile; loaded: boolean; version: number };
export const useBrand = create<BrandState>(() => ({ brand: DEFAULT_BRAND, loaded: false, version: 0 }));

export async function loadBrand(): Promise<BrandProfile> {
  const st = useBrand.getState();
  if (st.loaded) return st.brand;
  const saved = await get<BrandProfile>(KEY, store()).catch(() => undefined);
  const brand = saved ? mergeSaved(saved) : await defaultBrand();
  useBrand.setState({ brand, loaded: true });
  return brand;
}

export async function saveBrand(brand: BrandProfile): Promise<void> {
  useBrand.setState((s) => ({ brand, loaded: true, version: s.version + 1 }));
  await set(KEY, brand, store()).catch(() => undefined);
}

/** Reads an image file as a logo (PNG transparency is kept). */
export async function logoFromFile(file: Blob): Promise<BrandLogo> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const bmp = await createImageBitmap(new Blob([bytes]));
  const logo = { bytes, type: file.type || 'image/png', w: bmp.width, h: bmp.height };
  bmp.close();
  return logo;
}

const hex2 = (v: number) =>
  Math.round(Math.min(1, Math.max(0, v)) * 255)
    .toString(16)
    .padStart(2, '0');
export const rgbHex = (c: [number, number, number]) => '#' + c.map(hex2).join('');
export const hexRgb = (h: string): [number, number, number] =>
  [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16) / 255) as [number, number, number];
